import React, { useState, useEffect, Fragment, useMemo } from "react";
import {
  Dialog,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";

const EditDentalPaymentDialog = ({ open, onClose, payment, token }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    treatment: null,
    amount: "",
    currency: 1, // Default to AFN
    exchangeRate: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [formErrors, setFormErrors] = useState({});
  const [treatmentData, setTreatmentData] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  const currencyOptions = [
    { value: 1, label: "؋ AFN", symbol: "؋" },
    { value: 2, label: "$ USD", symbol: "$" },
    { value: 3, label: "€ EUR", symbol: "€" },
  ];

  const getCurrencySymbol = (currency) => {
    const option = currencyOptions.find((opt) => opt.value === currency);
    return option ? option.symbol : "؋";
  };

  const { data: treatments, loading: loadingTreatments } = useFetchObjects(
    ["dental-treatments"],
    "dental-treatments/?status_not=4",
    token
  );

  const {
    handleUpdate,
    loading: updating,
    success,
  } = useUpdate("dental-payments", token);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success]);

  // Format treatment data for autocomplete
  const formattedTreatmentData = useMemo(() => {
    if (!treatments || !Array.isArray(treatments)) return [];
    return treatments.map((treatment) => ({
      id: treatment.id,
      label: `${treatment.patient_name} - ${treatment.treatment_date}`,
      total_cost: treatment.total_cost,
      total_paid: treatment.total_paid,
      balance_due: treatment.balance_due,
    }));
  }, [treatments]);

  // Initialize form data when payment prop changes
  useEffect(() => {
    if (payment && treatments) {
      const selected = treatments.find((item) => item.id === payment.treatment);
      if (selected) {
        setSelectedTreatment({
          id: selected.id,
          label: `${selected.patient_name} - ${selected.treatment_date}`,
          total_cost: selected.total_cost,
          total_paid: selected.total_paid,
          balance_due: selected.balance_due,
        });
        setFormData({
          treatment: selected.id,
          amount: payment.amount || "",
          currency: payment.currency || 1,
          exchangeRate: payment.exchangeRate || "",
          date: payment.date || new Date().toISOString().split("T")[0],
        });
      }
    }
  }, [payment, treatments]);

  const handleSave = (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.treatment)
      errors.treatment = t("reception.treatmentRequired");
    if (!formData.amount || formData.amount <= 0)
      errors.amount = t("reception.amountRequired");
    else if (
      selectedTreatment &&
      parseFloat(formData.amount) > parseFloat(selectedTreatment.total_cost)
    ) {
      errors.amount = t("reception.amountExceedsBalanceDue");
    }
    if (!formData.date) errors.date = t("reception.dateRequired");

    // Validate exchange rate for non-AFN currencies
    if (formData.currency !== 1) {
      if (!formData.exchangeRate || parseFloat(formData.exchangeRate) <= 0) {
        errors.exchangeRate = t("reception.exchangeRateRequired");
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
    } else {
      // Convert exchangeRate to exchange_rate for backend
      const apiData = {
        ...formData,
        exchange_rate: formData.exchangeRate,
      };
      delete apiData.exchangeRate; // Remove the camelCase version
      handleUpdate(payment.id, apiData);
    }
  };

  const handleTreatmentSelect = (value) => {
    setFormData({ ...formData, treatment: value?.id || null });
    setSelectedTreatment(value);
    setFormErrors({ ...formErrors, treatment: "" });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box
        sx={{
          background: "linear-gradient(to bottom, #f9fafa, #ffffff)",
          py: 4,
          minHeight: "60vh",
          width: "100%",
          p: 2,
        }}
      >
        {/* Go Back Icon and Title */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("reception.goBack")} arrow>
            <IconButton
              onClick={onClose}
              aria-label={t("reception.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box mb={6} textAlign="center">
          <Typography
            variant="h3"
            fontWeight={700}
            color="primary"
            gutterBottom
            sx={{ fontSize: isSmallScreen ? "1.8rem" : "2.5rem" }}
          >
            {t("reception.editDentalPayment")}
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {/* Payment Form Section */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 3,
                boxShadow: 3,
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={600}
                color="primary"
                mb={2}
                sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
              >
                {t("reception.paymentForm")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSave}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {loadingTreatments ? (
                      <CircularProgress />
                    ) : (
                      <Autocomplete
                        options={formattedTreatmentData}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => handleTreatmentSelect(value)}
                        value={selectedTreatment}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("reception.selectTreatment")}
                            variant="outlined"
                            error={!!formErrors.treatment}
                            helperText={formErrors.treatment}
                            fullWidth
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.amount")}
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {getCurrencySymbol(formData.currency)}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t("reception.currency")}</InputLabel>
                      <Select
                        value={formData.currency}
                        onChange={(e) =>
                          setFormData({ ...formData, currency: e.target.value })
                        }
                        label={t("reception.currency")}
                      >
                        {currencyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {formData.currency !== 1 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("reception.exchangeRate")}
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={formData.exchangeRate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            exchangeRate: e.target.value,
                          })
                        }
                        error={!!formErrors.exchangeRate}
                        helperText={
                          formErrors.exchangeRate ||
                          t("reception.exchangeRateHelp")
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              1 {getCurrencySymbol(formData.currency)} =
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">؋</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("reception.date")}
                      variant="outlined"
                      fullWidth
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                    />
                  </Grid>
                </Grid>

                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={updating}
                    sx={{
                      fontSize: isSmallScreen ? "0.9rem" : "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {updating ? (
                      <CircularProgress size={20} />
                    ) : (
                      t("reception.save")
                    )}
                  </Button>
                </Box>
              </form>
            </Card>
          </Grid>

          {/* Treatment Details Section */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 3,
                boxShadow: 3,
                overflow: "hidden",
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary"
                  mb={2}
                  sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
                >
                  {t("reception.treatmentDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {selectedTreatment ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.patient")}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedTreatment.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.totalCost")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedTreatment.total_cost}؋
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.totalPaid")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedTreatment.total_paid}؋
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("reception.balanceDue")}
                      </Typography>
                      <Typography variant="h6" color="error">
                        {selectedTreatment.balance_due}؋
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography textAlign="center" color="textSecondary">
                    {t("reception.noTreatmentSelected")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default EditDentalPaymentDialog;
